import { type FC, type PropsWithChildren, useEffect } from 'react'
import { isAndroid, isIOS } from 'react-device-detect'

import {
  getCurrentNativeAppVersionFromUserAgent,
  isFeatureEnabledWithVersion,
  useNativeBridge,
} from '@dhl/native-bridge'
import {
  consentGroups,
  getConsentedGroups,
  OneTrustProvider,
  useOneTrustContext,
  useUpdateLanguageOnChange,
} from '@dhl/onetrust'
import { isNative } from '@dhl/webview'

import { useAdobeTagmanagerScript } from './analytics/adobe/useAdobeTagmanagerScript.js'
import { useGtmScript } from './analytics/gtm/useGtmScript.js'
import { useVirtualPageViews } from './analytics/gtm/useVirtualPageViews.js'
import { useDataLayerForProvider } from './analytics/useDataLayerForProvider.js'
import { type DatadogConfig, initDatadog, setDatadogTrackingConsent } from './datadog/datadog.js'
import { NativeTracking } from './NativeTracking.js'

const minAppVersionRequired = '5.6.0'

export type ThirdPartyConfig = {
  oneTrustId?: string
  oneTrustNativeEnabled?: boolean
  oneTrustNativeEnabledFromVersion?: string
  gmtId?: string
  virtualPageRootPath?: string
  nonce: string
  isLoggedIn?: boolean
  adobeTagManagerUrl?: string
  topLevelDomain?: string
}

type Props = {
  config: ThirdPartyConfig
  datadogConfig?: DatadogConfig
  onEnableTracking?: () => void
}

export const ThirdParty: FC<PropsWithChildren<Props>> = ({
  children,
  onEnableTracking,
  config: {
    oneTrustId,
    oneTrustNativeEnabled,
    oneTrustNativeEnabledFromVersion,
    gmtId,
    nonce,
    virtualPageRootPath,
    isLoggedIn = false,
    adobeTagManagerUrl,
    topLevelDomain = 'NL',
  },
  datadogConfig,
}) => {
  const { isTrackingAllowed: isNativeTrackingAllowed, requestNativeTrackingPermission } = useNativeBridge()
  const { isOneTrustLoaded } = useOneTrustContext()

  const shouldEnableNativeOneTrustForAppVersion =
    isNative() &&
    !!oneTrustNativeEnabled &&
    isFeatureEnabledWithVersion(oneTrustNativeEnabledFromVersion ?? '', getCurrentNativeAppVersionFromUserAgent())

  const isOneTrustEnabledInBrowser = !isNative() && !!oneTrustId
  const isOneTrustEnabledInNative =
    !!oneTrustId &&
    ((shouldEnableNativeOneTrustForAppVersion && isAndroid) ||
      (shouldEnableNativeOneTrustForAppVersion && isIOS && isNativeTrackingAllowed))

  const isOneTrustEnabled = isOneTrustEnabledInBrowser || isOneTrustEnabledInNative

  const isTrackingEnabled = isOneTrustEnabled ?? false

  useGtmScript(gmtId, nonce, isTrackingEnabled)

  useAdobeTagmanagerScript(adobeTagManagerUrl, nonce, isTrackingEnabled)

  useVirtualPageViews(!!gmtId && isTrackingEnabled, virtualPageRootPath, isLoggedIn)

  useUpdateLanguageOnChange(isTrackingEnabled)

  const shouldEnableTrackingScreen = isFeatureEnabledWithVersion(
    minAppVersionRequired,
    getCurrentNativeAppVersionFromUserAgent()
  )

  const showTrackingScreen = shouldEnableTrackingScreen && isNativeTrackingAllowed === undefined && isIOS

  useEffect(() => {
    if (showTrackingScreen) {
      requestNativeTrackingPermission()
    }
  }, [requestNativeTrackingPermission, showTrackingScreen])

  // enable internal datalayer to provide data
  useDataLayerForProvider({
    meta: {
      isLoggedIn,
      topLevelDomain,
    },
    providers: {
      isAdobeEnabled: Boolean(adobeTagManagerUrl && isOneTrustEnabled),
    },
  })

  useEffect(() => {
    if (!isTrackingEnabled) {
      return
    }
    onEnableTracking?.()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we only want to listen to tracking enable
  }, [isTrackingEnabled])

  useEffect(() => {
    if (datadogConfig) {
      initDatadog({
        ...datadogConfig,
        trackingConsent: getConsentedGroups().includes(consentGroups.performance) ? 'granted' : 'not-granted',
      })
    }
  }, [datadogConfig])

  useEffect(() => {
    if (datadogConfig && isOneTrustLoaded) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- explicit OneTrust event type not available
      window.OneTrust?.OnConsentChanged?.((event: any) => {
        const consent = event.detail?.includes(consentGroups.performance) ? 'granted' : 'not-granted'
        setDatadogTrackingConsent(consent)
      })
    }
  }, [datadogConfig, isOneTrustLoaded])

  return (
    <OneTrustProvider id={oneTrustId} nonce={nonce} shouldInitalize={isTrackingEnabled}>
      {children}
      {isOneTrustEnabledInNative && <NativeTracking />}
    </OneTrustProvider>
  )
}
